import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { PendingApprovalNotice } from '@/features/approvals';
import { ApiError } from '@/shared/api/api-error';

import { useRequestChannelFees, useUpdatePaymentChannel } from '../api/pricing.queries';
import type { PaymentChannel } from '../types/pricing.types';

const REASON_CLASS =
  'border-input bg-background focus-visible:ring-ring w-full rounded-md border px-2 py-1.5 text-xs focus-visible:ring-2 focus-visible:outline-none';

/**
 * Edit a payment channel (pricing.config.manage). Names and icon apply at once;
 * the position changes only through the country's checkout order; fees change
 * the customer price, so they are only requested and an Admin approves them.
 * Mount only while open.
 */
export function ChannelEditDialog({
  countryId,
  channel,
  onClose,
}: {
  countryId: number;
  channel: PaymentChannel;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Edit {channel.name}</DialogTitle>
          <DialogDescription className="text-xs">
            Recorded in the audit log with your reason.
          </DialogDescription>
        </DialogHeader>
        <DetailsForm countryId={countryId} channel={channel} onDone={onClose} />
        <Separator />
        <FeesForm countryId={countryId} channel={channel} onDone={onClose} />
      </DialogContent>
    </Dialog>
  );
}

function DetailsForm({
  countryId,
  channel,
  onDone,
}: {
  countryId: number;
  channel: PaymentChannel;
  onDone: () => void;
}) {
  const [name, setName] = useState(channel.name);
  const [arName, setArName] = useState(channel.arName ?? '');
  const [icon, setIcon] = useState(channel.icon ?? '');
  const [reason, setReason] = useState('');
  const update = useUpdatePaymentChannel(countryId, channel.id);
  const valid = name.trim() !== '' && arName.trim() !== '' && reason.trim().length >= 3;

  return (
    <form
      className="space-y-3 text-xs"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        update.mutate(
          {
            name: name.trim(),
            arName: arName.trim(),
            icon: icon.trim() || null,
            reason: reason.trim(),
          },
          { onSuccess: onDone },
        );
      }}
    >
      <p className="font-medium">Shown to customers</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id="channel-name" label="Name (English)" value={name} onChange={setName} />
        <Field
          id="channel-ar-name"
          label="Name (Arabic)"
          value={arName}
          onChange={setArName}
          dir="rtl"
        />
        <Field id="channel-icon" label="Icon" value={icon} onChange={setIcon} />
      </div>
      <p className="text-muted-foreground">
        Position {channel.sortOrder} at checkout — change it with &ldquo;Checkout order&rdquo;.
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="channel-details-reason" className="text-xs">
          Reason <span className="text-muted-foreground font-normal">(required)</span>
        </Label>
        <textarea
          id="channel-details-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          maxLength={500}
          className={REASON_CLASS}
        />
      </div>
      {update.error instanceof ApiError && (
        <p role="alert" className="text-destructive">
          {update.error.message}
        </p>
      )}
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={!valid || update.isPending}>
          Save details
        </Button>
      </div>
    </form>
  );
}

function FeesForm({
  countryId,
  channel,
  onDone,
}: {
  countryId: number;
  channel: PaymentChannel;
  onDone: () => void;
}) {
  const [percentFee, setPercentFee] = useState(String(channel.percentFee));
  const [fixedFee, setFixedFee] = useState(String(channel.fixedFee));
  const [reason, setReason] = useState('');
  const request = useRequestChannelFees(countryId, channel.id);
  const percent = Number(percentFee);
  const fixed = Number(fixedFee);
  const pendingApprovalId = channel.pendingFeesApprovalId ?? null;
  const valid =
    Number.isFinite(percent) &&
    percent >= 0 &&
    percent <= 20 &&
    Number.isFinite(fixed) &&
    fixed >= 0 &&
    (percent !== channel.percentFee || fixed !== channel.fixedFee) &&
    reason.trim().length >= 3;

  return (
    <form
      className="space-y-3 text-xs"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        request.mutate(
          { percentFee: percent, fixedFee: fixed, reason: reason.trim() },
          { onSuccess: onDone },
        );
      }}
    >
      <p className="font-medium">Fees</p>
      <p className="text-muted-foreground">
        Fees are part of the customer price, so a change goes to an Admin for approval and applies
        only then.
      </p>
      {pendingApprovalId !== null ? (
        <PendingApprovalNotice approvalId={pendingApprovalId} type="PaymentChannelFees">
          Waiting for approval: {channel.pendingFeesSummary}
        </PendingApprovalNotice>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              id="channel-percent-fee"
              label="Percentage fee (%)"
              value={percentFee}
              onChange={setPercentFee}
              type="number"
            />
            <Field
              id="channel-fixed-fee"
              label="Fixed fee"
              value={fixedFee}
              onChange={setFixedFee}
              type="number"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="channel-fees-reason" className="text-xs">
              Reason <span className="text-muted-foreground font-normal">(required)</span>
            </Label>
            <textarea
              id="channel-fees-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              maxLength={500}
              className={REASON_CLASS}
            />
          </div>
          {request.error instanceof ApiError && (
            <p role="alert" className="text-destructive">
              {request.error.message}
            </p>
          )}
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={!valid || request.isPending}>
              Send fees for approval
            </Button>
          </div>
        </>
      )}
    </form>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  dir,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number';
  dir?: 'rtl';
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        step={type === 'number' ? 'any' : undefined}
        value={value}
        dir={dir}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 text-xs"
      />
    </div>
  );
}
